import type { ApiClient } from '@tadpoles/api-client'

import type { AppContext } from '../context/app-context.svelte.js'
import { isSameSessionContext, type SessionContext } from '../context/session-context.js'
import type { UserContext } from '../context/user-context.js'
import type {
    AuthenticationService,
    AuthenticationState
} from './authentication/authentication.svelte.js'
import type { FamilyLocalBacking } from './persistence/family-local-backing.js'
import type { ParametersService, ParametersSnapshot } from './parameters.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import type { FamilyLocaleService } from './family-locale.svelte.js'
import { UserContextCache } from './user-context-cache.js'
import type {
    CurrentUserScopedServices,
    UserScopedServiceGraph
} from './user-scoped-service-scope.js'
import {
    UserScopedServices,
    type UserScopedProfileUpdate,
    type UserScopedServicesOptions
} from './user-scoped-services.svelte.js'

export type UserScopedServicesUnavailableState = {
    phase: 'unavailable'
}

export type UserScopedServicesInitializingState = {
    phase: 'initializing'
    sessionContext: SessionContext
}

export type UserScopedServicesAvailableState = {
    phase: 'available'
    services: UserScopedServices
    sessionContext: SessionContext
}

export type UserScopedServicesErrorState = {
    errorMessage: string
    phase: 'error'
    sessionContext: SessionContext
}

export type UserScopedServicesManagerState =
    | UserScopedServicesUnavailableState
    | UserScopedServicesInitializingState
    | UserScopedServicesAvailableState
    | UserScopedServicesErrorState

export type UserScopedServicesFactory = (
    context: UserContext,
    localBacking: ReturnType<FamilyLocalBacking['forUser']>,
    options: UserScopedServicesOptions
) => UserScopedServices

export type UserScopedServicesManagerOptions = {
    apiClient: ApiClient
    appContext: AppContext
    auth: Pick<AuthenticationService, 'getState' | 'subscribe'>
    localBacking: FamilyLocalBacking
    locale?: FamilyLocaleService
    parameters: ParametersService
    servicesFactory?: UserScopedServicesFactory
    userContextCache?: UserContextCache
}

export type UserScopedServicesTeardownResult = {
    currentUserPersistenceCleared: boolean
}

export class UserScopedServicesManager implements CurrentUserScopedServices, ServiceLifecycle {
    readonly #apiClient: ApiClient
    readonly #appContext: AppContext
    readonly #auth: Pick<AuthenticationService, 'getState' | 'subscribe'>
    readonly #localBacking: FamilyLocalBacking
    readonly #locale: FamilyLocaleService | undefined
    readonly #parameters: ParametersService
    readonly #servicesFactory: UserScopedServicesFactory
    readonly #userContextCache: UserContextCache
    #destroyed = false
    #initialized = false
    #invalidCacheSessionContext: SessionContext | undefined
    #lifecyclePhase: 'active' | 'suspended' = 'active'
    #loadPromise: Promise<boolean> | undefined
    #loadSessionContext: SessionContext | undefined
    #mismatchRecoveryPromise: Promise<boolean> | undefined
    #mismatchRecoverySessionContext: SessionContext | undefined
    #operationController = new AbortController()
    #services: UserScopedServices | undefined
    #teardownPromise: Promise<UserScopedServicesTeardownResult> = Promise.resolve({
        currentUserPersistenceCleared: true
    })
    #unsubscribeFromAuth: (() => void) | undefined
    #state = $state.raw<UserScopedServicesManagerState>({
        phase: 'unavailable'
    })

    public constructor(options: UserScopedServicesManagerOptions) {
        this.#apiClient = options.apiClient
        this.#appContext = options.appContext
        this.#auth = options.auth
        this.#localBacking = options.localBacking
        this.#locale = options.locale
        this.#parameters = options.parameters
        this.#userContextCache =
            options.userContextCache ?? new UserContextCache(options.localBacking)
        this.#servicesFactory =
            options.servicesFactory ??
            ((context, localBacking, servicesOptions) =>
                new UserScopedServices(context, localBacking, servicesOptions))
    }

    public get state(): UserScopedServicesManagerState {
        return this.#state
    }

    public get currentUserServices(): UserScopedServiceGraph | undefined {
        const state = this.#state

        return state.phase === 'available' ? state.services : undefined
    }

    public async initialize(): Promise<void> {
        if (this.#destroyed) {
            throw new Error('Cannot initialize a destroyed user-scoped services manager.')
        }

        if (this.#initialized) {
            return
        }

        this.#initialized = true
        this.#unsubscribeFromAuth = this.#auth.subscribe(({ current, previous }) => {
            this.#handleAuthenticationTransition(current, previous)
        })

        const authState = this.#auth.getState()
        if (authState.phase === 'authenticated') {
            await this.ensureAvailable(authState.sessionContext)
        }
    }

    public async resume(): Promise<void> {
        this.#lifecyclePhase = 'active'

        if (this.#state.phase !== 'available') {
            return
        }

        await this.#applyLifecyclePhase(this.#state.services, 'active')
    }

    public async suspend(): Promise<void> {
        this.#lifecyclePhase = 'suspended'

        if (this.#state.phase !== 'available') {
            return
        }

        await this.#applyLifecyclePhase(this.#state.services, 'suspended')
    }

    public waitForCurrentSessionTeardown(): Promise<UserScopedServicesTeardownResult> {
        return this.#teardownPromise
    }

    public async destroy(): Promise<void> {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#initialized = false
        this.#unsubscribeFromAuth?.()
        this.#unsubscribeFromAuth = undefined
        this.#operationController.abort()
        const loadPromise = this.#loadPromise
        const mismatchRecoveryPromise = this.#mismatchRecoveryPromise
        this.#state = {
            phase: 'unavailable'
        }
        await this.#teardownPromise
        await loadPromise
        await mismatchRecoveryPromise
        await this.#unpublishAndDestroy()
    }

    public async ensureAvailable(sessionContext: SessionContext): Promise<boolean> {
        if (this.#destroyed) {
            return false
        }

        await this.#teardownPromise

        if (this.#destroyed || !this.#isCurrentAuthenticatedSession(sessionContext)) {
            return false
        }

        if (
            this.#state.phase === 'available' &&
            isSameSessionContext(this.#state.sessionContext, sessionContext)
        ) {
            return true
        }

        if (
            this.#mismatchRecoveryPromise !== undefined &&
            this.#mismatchRecoverySessionContext !== undefined &&
            isSameSessionContext(this.#mismatchRecoverySessionContext, sessionContext)
        ) {
            const recovered = await this.#mismatchRecoveryPromise
            if (recovered) {
                return true
            }

            if (this.#destroyed || !this.#isCurrentAuthenticatedSession(sessionContext)) {
                return false
            }
        }

        if (
            this.#loadPromise !== undefined &&
            this.#loadSessionContext !== undefined &&
            isSameSessionContext(this.#loadSessionContext, sessionContext)
        ) {
            return await this.#loadPromise
        }

        const operationController = this.#replaceOperationController()
        this.#state = {
            phase: 'initializing',
            sessionContext
        }
        const loadPromise = this.#resolveAndActivate(sessionContext, operationController)
        this.#loadPromise = loadPromise
        this.#loadSessionContext = sessionContext

        try {
            return await loadPromise
        } finally {
            if (this.#loadPromise === loadPromise) {
                this.#loadPromise = undefined
                this.#loadSessionContext = undefined
            }
        }
    }

    public async reconcileProfileUpdate(
        sessionContext: SessionContext,
        personKey: string,
        update: UserScopedProfileUpdate
    ): Promise<boolean> {
        return await this.#reconcileUserUpdate(sessionContext, personKey, (services) => {
            services.applyProfileUpdate(update)
        })
    }

    public async reconcileLanguageUpdate(
        sessionContext: SessionContext,
        personKey: string,
        language: string,
        signal: AbortSignal
    ): Promise<boolean> {
        return await this.#reconcileUserUpdate(
            sessionContext,
            personKey,
            (services) => {
                services.applyLanguageUpdate(language)
            },
            signal
        )
    }

    #reconcileUserUpdate(
        sessionContext: SessionContext,
        personKey: string,
        applyUpdate: (services: UserScopedServices) => void,
        signal?: AbortSignal
    ): boolean {
        const services = this.#services

        if (
            this.#destroyed ||
            !this.#isCurrentAuthenticatedSession(sessionContext) ||
            services === undefined ||
            services.context.person.key !== personKey ||
            signal?.aborted
        ) {
            return false
        }

        const operationController = this.#replaceOperationController()
        applyUpdate(services)
        this.#cacheUserContext(sessionContext, services.context, operationController)
        this.#syncAppContext(services)
        return true
    }

    async #resolveAndActivate(
        sessionContext: SessionContext,
        operationController: AbortController
    ): Promise<boolean> {
        try {
            if (this.#isCacheInvalidFor(sessionContext)) {
                const snapshot = await this.#parameters.fetch(
                    sessionContext,
                    operationController.signal
                )
                this.#assertCurrentOperation(operationController)
                return await this.#startMismatchedCacheRecovery(
                    sessionContext,
                    snapshot,
                    operationController
                )
            }

            const cachedContext = await this.#userContextCache.restore(
                sessionContext,
                operationController.signal
            )
            this.#assertCurrentOperation(operationController)

            if (cachedContext !== null) {
                try {
                    await this.#activate(
                        sessionContext,
                        cachedContext,
                        undefined,
                        operationController
                    )
                } catch (error) {
                    if (operationController.signal.aborted) {
                        return false
                    }

                    console.warn('Unable to initialize cached user services; using network.', error)
                    const snapshot = await this.#parameters.fetch(
                        sessionContext,
                        operationController.signal
                    )
                    this.#assertCurrentOperation(operationController)

                    if (cachedContext.person.key !== snapshot.context.person.key) {
                        return await this.#startMismatchedCacheRecovery(
                            sessionContext,
                            snapshot,
                            operationController
                        )
                    }

                    await this.#activate(
                        sessionContext,
                        snapshot.context,
                        snapshot,
                        operationController
                    )
                    return true
                }

                void this.#refresh(sessionContext, operationController)
                return true
            }

            const snapshot = await this.#parameters.fetch(
                sessionContext,
                operationController.signal
            )
            this.#assertCurrentOperation(operationController)
            await this.#activate(sessionContext, snapshot.context, snapshot, operationController)
            return true
        } catch (error) {
            if (operationController.signal.aborted) {
                return false
            }

            await this.#unpublishAndDestroy()
            this.#state = {
                errorMessage: this.#toErrorMessage(error),
                phase: 'error',
                sessionContext
            }
            return false
        }
    }

    async #activate(
        sessionContext: SessionContext,
        context: UserContext,
        snapshot: ParametersSnapshot | undefined,
        operationController: AbortController
    ): Promise<void> {
        const existingServices = this.#services

        if (existingServices?.context.person.key === context.person.key) {
            existingServices.replaceContext(context)
            if (snapshot) {
                existingServices.replaceAll(snapshot.data)
                this.#cacheUserContext(sessionContext, context, operationController)
            }
            this.#syncAppContext(existingServices)
            this.#state = {
                phase: 'available',
                services: existingServices,
                sessionContext
            }
            return
        }

        await this.#unpublishAndDestroy()
        this.#assertCurrentOperation(operationController)

        const services = this.#servicesFactory(context, this.#localBacking.forUser(context), {
            apiClient: this.#apiClient,
            restoreDomainData: snapshot === undefined
        })
        let adopted = false

        try {
            await services.initialize()
            this.#assertCurrentOperation(operationController)

            if (snapshot) {
                services.replaceAll(snapshot.data)
            }

            await this.#alignLifecycleBeforePublication(services, operationController)
            this.#services = services
            adopted = true
            if (snapshot) {
                this.#cacheUserContext(sessionContext, context, operationController)
            }
            this.#syncAppContext(services)
            this.#state = {
                phase: 'available',
                services,
                sessionContext
            }
        } finally {
            if (!adopted) {
                await this.#destroyServices(services)
            }
        }
    }

    async #refresh(
        sessionContext: SessionContext,
        operationController: AbortController
    ): Promise<boolean> {
        try {
            const snapshot = await this.#parameters.fetch(
                sessionContext,
                operationController.signal
            )

            if (!this.#isCurrentOperation(operationController)) {
                return false
            }

            const services = this.#services
            if (services === undefined) {
                return false
            }

            if (services.context.person.key !== snapshot.context.person.key) {
                return await this.#startMismatchedCacheRecovery(
                    sessionContext,
                    snapshot,
                    operationController
                )
            }

            services.replaceContext(snapshot.context)
            services.replaceAll(snapshot.data)
            this.#cacheUserContext(sessionContext, snapshot.context, operationController)
            this.#syncAppContext(services)
            this.#state = {
                phase: 'available',
                services,
                sessionContext
            }
            return true
        } catch (error) {
            if (!operationController.signal.aborted) {
                console.warn('Unable to refresh user-scoped services.', error)
            }
            return false
        }
    }

    #startMismatchedCacheRecovery(
        sessionContext: SessionContext,
        snapshot: ParametersSnapshot,
        operationController: AbortController
    ): Promise<boolean> {
        const cachedServices = this.#services
        this.#invalidCacheSessionContext = sessionContext
        this.#services = undefined
        this.#appContext.clear()
        this.#state = {
            phase: 'initializing',
            sessionContext
        }

        const recoveryPromise = Promise.resolve().then(
            async () =>
                await this.#completeMismatchedCacheRecovery(
                    sessionContext,
                    snapshot,
                    cachedServices,
                    operationController
                )
        )
        this.#mismatchRecoveryPromise = recoveryPromise
        this.#mismatchRecoverySessionContext = sessionContext
        void recoveryPromise.finally(() => {
            if (this.#mismatchRecoveryPromise === recoveryPromise) {
                this.#mismatchRecoveryPromise = undefined
                this.#mismatchRecoverySessionContext = undefined
            }
        })
        return recoveryPromise
    }

    async #completeMismatchedCacheRecovery(
        sessionContext: SessionContext,
        snapshot: ParametersSnapshot,
        cachedServices: UserScopedServices | undefined,
        operationController: AbortController
    ): Promise<boolean> {
        if (cachedServices !== undefined) {
            await this.#destroyServices(cachedServices)
        }

        try {
            this.#assertCurrentOperation(operationController)
            await this.#userContextCache.clear()
            this.#assertCurrentOperation(operationController)
            await this.#activate(sessionContext, snapshot.context, snapshot, operationController)
            this.#invalidCacheSessionContext = undefined
            return true
        } catch (error) {
            if (operationController.signal.aborted) {
                return false
            }

            await this.#unpublishAndDestroy()
            this.#state = {
                errorMessage: this.#toErrorMessage(error),
                phase: 'error',
                sessionContext
            }
            console.warn('Unable to replace mismatched cached user services.', error)
            return false
        }
    }

    #cacheUserContext(
        sessionContext: SessionContext,
        context: UserContext,
        operationController: AbortController
    ): void {
        void this.#userContextCache.put(sessionContext, context).catch((error) => {
            if (!operationController.signal.aborted) {
                console.warn('Unable to persist the current user context.', error)
            }
        })
    }

    #handleAuthenticationTransition(
        current: AuthenticationState,
        previous: AuthenticationState
    ): void {
        const previousSession =
            previous.phase === 'authenticated' ? previous.sessionContext : undefined
        const currentSession =
            current.phase === 'authenticated' ? current.sessionContext : undefined

        if (
            previousSession !== undefined &&
            (currentSession === undefined || !isSameSessionContext(previousSession, currentSession))
        ) {
            this.#beginSessionTeardown()
        }

        if (
            currentSession !== undefined &&
            (previousSession === undefined ||
                !isSameSessionContext(previousSession, currentSession))
        ) {
            void this.ensureAvailable(currentSession)
        }
    }

    #beginSessionTeardown(): void {
        this.#operationController.abort()
        const mismatchRecoveryPromise = this.#mismatchRecoveryPromise
        this.#mismatchRecoveryPromise = undefined
        this.#mismatchRecoverySessionContext = undefined
        const services = this.#services
        this.#services = undefined
        this.#appContext.clear()
        this.#state = {
            phase: 'unavailable'
        }
        this.#teardownPromise = this.#teardownPromise.then(async () => {
            await mismatchRecoveryPromise

            if (services !== undefined) {
                await this.#destroyServices(services)
            }

            try {
                await this.#userContextCache.clear()
                this.#invalidCacheSessionContext = undefined
                return {
                    currentUserPersistenceCleared: true
                }
            } catch (error) {
                console.warn(
                    'Unable to clear current user persistence during session teardown.',
                    error
                )
                return {
                    currentUserPersistenceCleared: false
                }
            }
        })
    }

    async #unpublishAndDestroy(): Promise<void> {
        const services = this.#services
        this.#services = undefined
        this.#appContext.clear()

        if (services !== undefined) {
            await this.#destroyServices(services)
        }
    }

    async #destroyServices(services: UserScopedServices): Promise<void> {
        try {
            await services.destroy()
        } catch (error) {
            console.warn('Unable to destroy all user-scoped services.', error)
        }
    }

    #syncAppContext(services: UserScopedServices): void {
        this.#appContext.setPerson(services.context.person)
        this.#locale?.setFromAccountPreference(services.context.person.language)
        const currentChildKey = this.#appContext.child?.key
        const selectedChild = currentChildKey ? services.child.get(currentChildKey) : null
        this.#appContext.setChild(selectedChild ?? services.child.children[0])
    }

    #replaceOperationController(): AbortController {
        this.#operationController.abort()
        this.#operationController = new AbortController()
        return this.#operationController
    }

    #isCurrentAuthenticatedSession(sessionContext: SessionContext): boolean {
        const authState = this.#auth.getState()
        return (
            authState.phase === 'authenticated' &&
            isSameSessionContext(authState.sessionContext, sessionContext)
        )
    }

    #isCacheInvalidFor(sessionContext: SessionContext): boolean {
        return (
            this.#invalidCacheSessionContext !== undefined &&
            isSameSessionContext(this.#invalidCacheSessionContext, sessionContext)
        )
    }

    async #alignLifecycleBeforePublication(
        services: UserScopedServices,
        operationController: AbortController
    ): Promise<void> {
        let servicesPhase: 'active' | 'suspended' = 'active'

        while (servicesPhase !== this.#lifecyclePhase) {
            const targetPhase: 'active' | 'suspended' = this.#lifecyclePhase

            await this.#applyLifecyclePhase(services, targetPhase)

            this.#assertCurrentOperation(operationController)
            servicesPhase = targetPhase
        }
    }

    async #applyLifecyclePhase(
        services: UserScopedServices,
        phase: 'active' | 'suspended'
    ): Promise<void> {
        try {
            if (phase === 'suspended') {
                await services.suspend()
            } else {
                await services.resume()
            }
        } catch (error) {
            console.warn(
                phase === 'suspended'
                    ? 'Unable to suspend all user-scoped services.'
                    : 'Unable to resume all user-scoped services.',
                error
            )
        }
    }

    #assertCurrentOperation(operationController: AbortController): void {
        if (!this.#isCurrentOperation(operationController)) {
            throw new DOMException('User service operation superseded.', 'AbortError')
        }
    }

    #isCurrentOperation(operationController: AbortController): boolean {
        return (
            !operationController.signal.aborted && this.#operationController === operationController
        )
    }

    #toErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message
        }

        return 'Unable to initialize user services.'
    }
}
